import type { CircleHitObject, CirclesChart } from '@shared/circlesChart';
import type { CirclesEngineState } from '@shared/engine/circles/state';
import type { PlayfieldRect } from '../circlesPlayfieldGeometry';
import { getStackOffsets } from '../circlesStacking';
import { comboColour, hexToRgba } from './circlesRenderColour';

/**
 * Spacing between dots in osu units. 32 matches osu!'s default
 * `FOLLOW_POINT_DISTANCE`; the dot count then falls out of the
 * pair's gap distance.
 */
const FOLLOWPOINT_SPACING_OSU = 32;

/**
 * Skip the trail when the next circle sits within this many radii of
 * the previous one — the eye doesn't need a guide to tell it about a
 * stack.
 */
const FOLLOWPOINT_MIN_RADII = 1.5;

/**
 * Followpoints renderer. Owns a monotonic `firstPairIdx` cursor into
 * the beat-sorted hit-object view passed in by {@link HitObjectsPass}
 * so per-frame walk costs O(visible pairs) instead of O(all hit
 * objects). A 220-object extreme chart used to iterate every pair
 * every frame (~13 k ops/sec) even though the average frame only
 * paints ~5 followpoint pairs.
 *
 * The cursor is monotonic — once a pair's "next" circle is past
 * `-okWindowBeats - 0.001` (a small fudge so a circle sitting on the
 * cutoff doesn't get advanced past while still painting) it never
 * becomes visible again, so the cursor advances past it permanently.
 * Reset by {@link reset} on loop wrap.
 *
 * Followpoints: a series of small dots leading from the previous
 * circle in the same combo group to the next one. Mirrors osu!'s
 * iconic "track the cursor path" guide — players read the next
 * note by glancing along the dot trail instead of hunting the
 * playfield.
 *
 * Rules (mirror osu! lazer's `FollowPointConnection`):
 *  - Pair consecutive non-spinner hit objects.
 *  - Skip if the next object starts a new combo (`newCombo`).
 *  - Skip if the two objects are within ~1.5× circle-radius of
 *    each other (the visual stack already conveys the relationship).
 *  - Fade dots in/out around the *next* circle's approach window —
 *    they appear as the next circle becomes visible and finish
 *    fading just as it hits.
 *  - Skip stacks: if the next circle is offset by stacking, the
 *    followpoint chases the offset position so the trail visually
 *    aligns with where the player sees the circle.
 */
export class FollowpointRenderer {
  /** Index of the earliest "next" circle that might still need a trail this frame. */
  private firstPairIdx = 1;

  reset(): void {
    this.firstPairIdx = 1;
  }

  draw(
    ctx: CanvasRenderingContext2D,
    chart: CirclesChart,
    sortedHitObjects: ReadonlyArray<CircleHitObject>,
    state: CirclesEngineState,
    beat: number,
    approach: number,
    okWindowBeats: number,
    field: PlayfieldRect,
  ): void {
    const minDistSq =
      (chart.circleRadius * FOLLOWPOINT_MIN_RADII) * (chart.circleRadius * FOLLOWPOINT_MIN_RADII);
    const stackMap = getStackOffsets(chart);
    const objs = sortedHitObjects;
    if (objs.length < 2) {
      return;
    }
    // Advance the pair cursor past any "next" circle whose late
    // cutoff (`dist < -okWindowBeats`) is firmly behind the current
    // beat. Tiny 0.001 fudge so a circle sitting exactly on the
    // cutoff doesn't get advanced past while still painting.
    while (this.firstPairIdx < objs.length) {
      const next = objs[this.firstPairIdx];
      if (next.beat - beat < -okWindowBeats - 0.001) {
        this.firstPairIdx += 1;
      } else {
        break;
      }
    }
    // Cursor must stay ≥ 1 since pairs read (objs[i-1], objs[i]).
    const startIdx = Math.max(1, this.firstPairIdx);
    ctx.save();
    ctx.shadowBlur = 0;
    for (let i = startIdx; i < objs.length; i++) {
      const next = objs[i];
      if (next.kind === 'spinner') {
        continue;
      }
      if (next.newCombo) {
        continue;
      }
      const prev = objs[i - 1];
      if (prev.kind === 'spinner') {
        continue;
      }
      // Only render while the upcoming circle is in the approach
      // window. Fade-in matches the circle's own fade (linear ramp
      // over the back half of approach); fade-out kicks in once the
      // next circle is past its hit beat. The late-edge bound
      // matches the engine's clickability cutoff (okWindowBeats),
      // so a still-clickable circle keeps its trail until the
      // auto-miss expiry rolls over.
      const dist = next.beat - beat;
      if (dist > approach) {
        // Sorted by beat — every later pair is even further in the
        // future, none of them have visible trails yet.
        break;
      }
      if (dist < -okWindowBeats) {
        continue;
      }
      // The previous circle must already be resolved OR the player
      // is in the brief overlap where prev is fading out while next
      // is fading in. Either way the trail makes sense once the
      // previous note is past its visual peak.
      const prevResolved = state.resolvedIds.has(prev.id);
      const prevPastBeat = beat >= prev.beat;
      if (!prevResolved && !prevPastBeat) {
        continue;
      }
      const prevStack = stackMap.get(prev.id);
      const nextStack = stackMap.get(next.id);
      const px = prev.x + (prevStack?.dx ?? 0);
      const py = prev.y + (prevStack?.dy ?? 0);
      const nx = next.x + (nextStack?.dx ?? 0);
      const ny = next.y + (nextStack?.dy ?? 0);
      const dx = nx - px;
      const dy = ny - py;
      const distSq = dx * dx + dy * dy;
      if (distSq <= minDistSq) {
        continue;
      }
      const gapOsu = Math.sqrt(distSq);
      // Inset the start / end by one radius so the trail doesn't sit
      // on the circle bodies — gives the dots clean breathing room.
      const inset = chart.circleRadius;
      const usable = gapOsu - inset * 2;
      if (usable <= 0) {
        continue;
      }
      const dotCount = Math.max(1, Math.floor(usable / FOLLOWPOINT_SPACING_OSU));
      const ux = dx / gapOsu;
      const uy = dy / gapOsu;
      // Alpha ramps with how visible the next circle is — same shape
      // as the circle's fade-in but slightly weaker (it's a hint,
      // not the focal point). Caps at 0.7 so the trail reads as a
      // clear guide without competing with the circles themselves.
      const tNorm = Math.max(0, Math.min(1, dist / approach));
      const fadeIn = 1 - tNorm;
      // Fade-out spans the late side of the OK window so the trail
      // is still readable as the player squeaks an OK-tier hit,
      // not gone the moment the perfect window closes.
      const fadeOut = dist < 0 ? Math.max(0, 1 + dist / okWindowBeats) : 1;
      const alpha = Math.min(0.7, fadeIn * fadeOut * 0.85);
      if (alpha <= 0.01) {
        continue;
      }
      const colour = comboColour(chart, next.comboIndex);
      const dotR = Math.max(1.6, 2.2 * field.scale);
      for (let d = 0; d < dotCount; d++) {
        // Stagger per-dot alpha by index so the dot closest to the
        // next circle is the most opaque — the eye flows from
        // faint→bright along the trail toward the target.
        const dotAlpha = alpha * (0.5 + 0.5 * ((d + 1) / dotCount));
        const offsetOsu = inset + (d + 0.5) * (usable / dotCount);
        const ox = px + ux * offsetOsu;
        const oy = py + uy * offsetOsu;
        const cx = field.x + ox * field.scale;
        const cy = field.y + oy * field.scale;
        ctx.fillStyle = hexToRgba(colour, dotAlpha);
        ctx.beginPath();
        ctx.arc(cx, cy, dotR, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.restore();
  }
}
